/*
 * This illustrates how to create a widget that makes use of a number of different field definition types.
 */

import { xHeaderActions, xHeaderState } from "@eisinc/App/Store/Reducers";
import { ButtonPresentationProps } from "@eisinc/MobiusOverrides/Button";
import Close from "@eisinc/XDL/Icons/Close";
import { color, layoutContainer, xScreen } from "@eisinc/XDL/Utilities";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { StyledButton } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { connect, DispatchProp } from "react-redux";
import { css } from "styled-components";

const enum fields {
    globalMessage = "globalMessage",
    backgroundColor = "backgroundColor",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.globalMessage]: string;
        [fields.backgroundColor]: string;
    };
}

const mapStateToProps = (state: xHeaderState) => ({
    isSticky: state.xHeader.isSticky,
    globalMessageState: state.xHeader.globalMessage,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> & DispatchProp<xHeaderActions>;

export interface GlobalWebsiteMessageStyles {
    container?: InjectableCss;
    messageWrapper?: InjectableCss;
    text?: TypographyPresentationProps;
    close?: ButtonPresentationProps;
    icon?: IconPresentationProps;
}

export const globalWebsiteMessageStyles: GlobalWebsiteMessageStyles = {
    container: {
        css: css`
            ${({ backgroundColor }: any) =>
                backgroundColor &&
                css`
                    background: ${backgroundColor};
                `}

            ${({ globalMessageState }: any) =>
                !globalMessageState &&
                css`
                    display: none;
                `}
        `,
    },
    messageWrapper: {
        css: css`
            ${layoutContainer}
            min-height: 33px;
            text-align: center;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 10px 32px;
        `,
    },
    text: {
        size: 12,
        color: "common.white",
        weight: "normal",
        lineHeight: "14px",
        css: css`
            width: 100%;
            margin-bottom: 0;

            & > ${TypographyStyle} {
                color: inherit;
                font-size: inherit;
                font-weight: inherit;
                line-height: inherit;
                margin-bottom: 0;
            }
            & ${StyledButton} > ${TypographyStyle} {
                font-size: inherit;
                font-weight: inherit;
                line-height: inherit;
                margin-bottom: 0;
            }

            ${xScreen("lte", "xs")} {
                text-align: left;
            }
        `,
    },
    icon: {
        size: 10,
        src: Close,
        color: "common.white",
    },
};

const styles = globalWebsiteMessageStyles;

const GlobalWebsiteMessage: React.FC<Props> = ({
    fields: { backgroundColor, globalMessage },
    dispatch,
    isSticky,
    globalMessageState,
}) => {
    const onClose = () => {
        dispatch({ type: "xHeader/globalMessage", globalMessage: false });
    };

    return (
        <StyledWrapper {...styles.container} {...({ backgroundColor, isSticky, globalMessageState } as any)}>
            <StyledWrapper {...styles.messageWrapper} as="aside" role="note">
                {globalMessage && <Typography {...styles.text}>{parse(globalMessage, parserOptions)}</Typography>}
                <Clickable
                    {...styles.close}
                    onClick={onClose}
                    data-test-selector="global_sitemessage_close"
                    aria-label={translate("Close Global Message")}
                >
                    <Icon {...styles.icon} aria-hidden="true" />
                </Clickable>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const definition: WidgetDefinition = {
    group: "Header",
    displayName: "Global Website Message",
    allowedContexts: ["Header"],
    fieldDefinitions: [
        {
            name: fields.globalMessage,
            displayName: "Global Message Content",
            editorTemplate: "RichTextField",
            defaultValue: "",
            fieldType: "General",
            placeholder: "your content goes here",
            isRequired: true,
        },
        {
            name: fields.backgroundColor,
            displayName: "Background Color",
            editorTemplate: "ColorPickerField",
            defaultValue: "",
            fieldType: "General",
            isRequired: true,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(GlobalWebsiteMessage),
    definition,
};

export default widgetModule;
